import React, { useState, useEffect } from 'react';

const Goals = ({ sessions, tasks }) => {
  const [goals, setGoals] = useState([]);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    type: 'time', // time, sessions, tasks, streak
    target: 100,
    timeframe: 'week', // day, week, month, year, custom
    subject: '',
    priority: 'medium',
    deadline: ''
  });

  const goalTypes = [
    { id: 'time', name: 'Study Time', unit: 'minutes', icon: '⏰' },
    { id: 'sessions', name: 'Study Sessions', unit: 'sessions', icon: '📚' },
    { id: 'tasks', name: 'Complete Tasks', unit: 'tasks', icon: '✅' },
    { id: 'streak', name: 'Study Streak', unit: 'days', icon: '🔥' },
    { id: 'pomodoros', name: 'Pomodoro Sessions', unit: 'pomodoros', icon: '🍅' },
    { id: 'subjects', name: 'Study Different Subjects', unit: 'subjects', icon: '🎯' }
  ];

  const timeframes = [
    { id: 'day', name: 'Daily', icon: '📅' },
    { id: 'week', name: 'Weekly', icon: '📊' },
    { id: 'month', name: 'Monthly', icon: '🗓️' },
    { id: 'year', name: 'Yearly', icon: '📈' },
    { id: 'custom', name: 'Custom Date', icon: '🎯' }
  ];

  useEffect(() => {
    const savedGoals = localStorage.getItem('studyGoals');
    if (savedGoals) {
      setGoals(JSON.parse(savedGoals));
    }
  }, []);

  const saveGoals = (updatedGoals) => {
    setGoals(updatedGoals);
    localStorage.setItem('studyGoals', JSON.stringify(updatedGoals));
  };

  const addGoal = () => {
    if (!newGoal.title.trim()) return;

    const goal = {
      id: Date.now(),
      ...newGoal,
      createdAt: new Date().toISOString(),
      completed: false,
      completedAt: null
    };

    saveGoals([...goals, goal]);
    setNewGoal({
      title: '',
      description: '',
      type: 'time',
      target: 100,
      timeframe: 'week',
      subject: '',
      priority: 'medium',
      deadline: ''
    });
    setShowAddGoal(false);
  };

  const deleteGoal = (goalId) => {
    saveGoals(goals.filter(g => g.id !== goalId));
  };

  const markGoalComplete = (goalId) => {
    const updatedGoals = goals.map(goal => {
      if (goal.id === goalId) {
        return {
          ...goal,
          completed: !goal.completed,
          completedAt: !goal.completed ? new Date().toISOString() : null
        };
      }
      return goal;
    });
    saveGoals(updatedGoals);
  };

  const calculateProgress = (goal) => {
    const now = new Date();
    let startDate = new Date(goal.createdAt);

    // Calculate timeframe boundaries
    switch (goal.timeframe) {
      case 'day':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        const dayOfWeek = now.getDay();
        startDate = new Date(now.getTime() - dayOfWeek * 24 * 60 * 60 * 1000);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'custom':
        startDate = new Date(goal.createdAt);
        break;
    }

    // Filter sessions based on timeframe and subject
    let relevantSessions = sessions.filter(session => {
      const sessionDate = new Date(session.completedAt);
      const withinTimeframe = sessionDate >= startDate;
      const matchesSubject = !goal.subject || session.subject === goal.subject;
      return withinTimeframe && matchesSubject;
    });

    // Filter tasks based on timeframe and subject
    let relevantTasks = tasks.filter(task => {
      if (!task.completed || !task.completedAt) return false;
      const taskDate = new Date(task.completedAt);
      const withinTimeframe = taskDate >= startDate;
      const matchesSubject = !goal.subject || task.subject === goal.subject;
      return withinTimeframe && matchesSubject;
    });

    let current = 0;

    switch (goal.type) {
      case 'time':
        current = relevantSessions.reduce((sum, s) => sum + s.duration, 0) / 60; // Convert to minutes
        break;
      case 'sessions':
        current = relevantSessions.length;
        break;
      case 'tasks':
        current = relevantTasks.length;
        break;
      case 'pomodoros':
        current = relevantSessions.filter(s => s.type === 'pomodoro').length;
        break;
      case 'subjects':
        const uniqueSubjects = new Set(relevantSessions.map(s => s.subject));
        current = uniqueSubjects.size;
        break;
      case 'streak':
        // Calculate current streak
        current = calculateCurrentStreak();
        break;
      default:
        current = 0;
    }

    return {
      current: Math.round(current * 100) / 100,
      percentage: Math.min((current / goal.target) * 100, 100)
    };
  };

  const calculateCurrentStreak = () => {
    if (sessions.length === 0) return 0;

    const studyDates = [...new Set(sessions.map(s => s.date))]
      .sort((a, b) => new Date(a) - new Date(b));

    let currentStreak = 1;
    let lastDate = new Date(studyDates[0]);

    for (let i = 1; i < studyDates.length; i++) {
      const currentDate = new Date(studyDates[i]);
      const daysDiff = Math.floor((currentDate - lastDate) / (1000 * 60 * 60 * 24));

      if (daysDiff === 1) {
        currentStreak++;
      } else if (daysDiff > 1) {
        currentStreak = 1;
      }
      lastDate = currentDate;
    }

    // Check if streak is still active
    const today = new Date();
    const lastStudyDate = new Date(studyDates[studyDates.length - 1]);
    const daysSinceLastStudy = Math.floor((today - lastStudyDate) / (1000 * 60 * 60 * 24));

    return daysSinceLastStudy <= 1 ? currentStreak : 0;
  };

  const isGoalOverdue = (goal) => {
    if (!goal.deadline) return false;
    return new Date(goal.deadline) < new Date() && !goal.completed;
  };

  const getDaysUntilDeadline = (goal) => {
    if (!goal.deadline) return null;
    const days = Math.ceil((new Date(goal.deadline) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#dc3545';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getGoalTypeInfo = (type) => {
    return goalTypes.find(gt => gt.id === type) || goalTypes[0];
  };

  const getTimeframeInfo = (timeframe) => {
    return timeframes.find(tf => tf.id === timeframe) || timeframes[0];
  };

  const activeGoals = goals.filter(g => !g.completed);
  const completedGoals = goals.filter(g => g.completed);

  return (
    <div className="goals">
      <div className="goals-header">
        <h2>🎯 Goals & Milestones</h2>
        <button
          onClick={() => setShowAddGoal(!showAddGoal)}
          className="add-goal-btn"
        >
          + Add Goal
        </button>
      </div>

      <div className="goals-summary">
        <div className="summary-card">
          <span className="summary-number">{activeGoals.length}</span>
          <span className="summary-label">Active Goals</span>
        </div>
        <div className="summary-card">
          <span className="summary-number">{completedGoals.length}</span>
          <span className="summary-label">Completed</span>
        </div>
        <div className="summary-card">
          <span className="summary-number">
            {activeGoals.filter(g => isGoalOverdue(g)).length}
          </span>
          <span className="summary-label">Overdue</span>
        </div>
      </div>

      {showAddGoal && (
        <div className="add-goal-form">
          <h3>Create New Goal</h3>

          <div className="form-row">
            <input
              type="text"
              placeholder="Goal title *"
              value={newGoal.title}
              onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
              className="goal-input"
            />
            <select
              value={newGoal.priority}
              onChange={(e) => setNewGoal({...newGoal, priority: e.target.value})}
              className="priority-select"
            >
              <option value="low">Low Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="high">High Priority</option>
            </select>
          </div>

          <textarea
            placeholder="Description (optional)"
            value={newGoal.description}
            onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
            className="goal-description"
            rows="2"
          />

          <div className="form-row">
            <select
              value={newGoal.type}
              onChange={(e) => setNewGoal({...newGoal, type: e.target.value})}
              className="goal-select"
            >
              {goalTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.icon} {type.name}
                </option>
              ))}
            </select>

            <input
              type="number"
              placeholder="Target"
              value={newGoal.target}
              onChange={(e) => setNewGoal({...newGoal, target: parseInt(e.target.value) || 1})}
              className="goal-input"
              min="1"
            />

            <select
              value={newGoal.timeframe}
              onChange={(e) => setNewGoal({...newGoal, timeframe: e.target.value})}
              className="goal-select"
            >
              {timeframes.map(tf => (
                <option key={tf.id} value={tf.id}>
                  {tf.icon} {tf.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <input
              type="text"
              placeholder="Subject (optional)"
              value={newGoal.subject}
              onChange={(e) => setNewGoal({...newGoal, subject: e.target.value})}
              className="goal-input"
            />
            {newGoal.timeframe === 'custom' && (
              <input
                type="date"
                value={newGoal.deadline}
                onChange={(e) => setNewGoal({...newGoal, deadline: e.target.value})}
                className="goal-input"
              />
            )}
          </div>

          <div className="form-actions">
            <button onClick={addGoal} className="save-btn">Create Goal</button>
            <button onClick={() => setShowAddGoal(false)} className="cancel-btn">Cancel</button>
          </div>
        </div>
      )}

      <div className="goals-list">
        <h3>🎯 Active Goals</h3>
        {activeGoals.length === 0 ? (
          <div className="no-goals">
            <p>No active goals yet.</p>
            <p>Create your first goal to start tracking your progress!</p>
          </div>
        ) : (
          activeGoals.map(goal => {
            const progress = calculateProgress(goal);
            const typeInfo = getGoalTypeInfo(goal.type);
            const timeframeInfo = getTimeframeInfo(goal.timeframe);
            const daysUntilDeadline = getDaysUntilDeadline(goal);

            return (
              <div
                key={goal.id}
                className={`goal-item ${isGoalOverdue(goal) ? 'overdue' : ''}`}
              >
                <div className="goal-header">
                  <div className="goal-title-section">
                    <h4 className="goal-title">{goal.title}</h4>
                    <div className="goal-badges">
                      <span
                        className="priority-badge"
                        style={{ backgroundColor: getPriorityColor(goal.priority) }}
                      >
                        {goal.priority}
                      </span>
                      <span className="type-badge">
                        {typeInfo.icon} {typeInfo.name}
                      </span>
                      <span className="timeframe-badge">
                        {timeframeInfo.icon} {timeframeInfo.name}
                      </span>
                    </div>
                  </div>
                  <div className="goal-actions">
                    <button
                      onClick={() => markGoalComplete(goal.id)}
                      className="complete-btn"
                      title="Mark as complete"
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => deleteGoal(goal.id)}
                      className="delete-btn"
                      title="Delete goal"
                    >
                      ×
                    </button>
                  </div>
                </div>

                {goal.description && (
                  <p className="goal-description">{goal.description}</p>
                )}

                <div className="goal-progress">
                  <div className="progress-header">
                    <span className="progress-text">
                      {progress.current} / {goal.target} {typeInfo.unit}
                    </span>
                    <span className="progress-percentage">
                      {Math.round(progress.percentage)}%
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progress.percentage}%` }}
                    ></div>
                  </div>
                </div>

                <div className="goal-meta">
                  {goal.subject && (
                    <span className="goal-subject">Subject: {goal.subject}</span>
                  )}
                  {daysUntilDeadline !== null && (
                    <span className={`deadline-info ${daysUntilDeadline < 0 ? 'overdue' : ''}`}>
                      {daysUntilDeadline < 0
                        ? `Overdue by ${Math.abs(daysUntilDeadline)} days`
                        : daysUntilDeadline === 0
                        ? 'Due today!'
                        : `${daysUntilDeadline} days remaining`
                      }
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {completedGoals.length > 0 && (
        <div className="completed-goals">
          <h3>✅ Completed Goals</h3>
          <div className="completed-list">
            {completedGoals.slice(0, 5).map(goal => {
              const typeInfo = getGoalTypeInfo(goal.type);
              return (
                <div key={goal.id} className="completed-goal-item">
                  <span className="completed-icon">🎉</span>
                  <span className="completed-title">{goal.title}</span>
                  <span className="completed-type">{typeInfo.icon} {goal.target} {typeInfo.unit}</span>
                  <span className="completed-date">
                    {new Date(goal.completedAt).toLocaleDateString()}
                  </span>
                </div>
              );
            })}
            {completedGoals.length > 5 && (
              <div className="more-completed">
                +{completedGoals.length - 5} more completed goals
              </div>
            )}
          </div>
        </div>
      )}

      <div className="goal-tips">
        <h4>💡 Goal Setting Tips</h4>
        <ul>
          <li>Make goals specific and measurable</li>
          <li>Set realistic but challenging targets</li>
          <li>Break large goals into smaller milestones</li>
          <li>Review and adjust goals regularly</li>
          <li>Celebrate when you achieve your goals!</li>
        </ul>
      </div>
    </div>
  );
};

export default Goals;